import { Badge } from "@/components/ui/badge";
import { TtOrderMetadata } from "@/lib/api/risk/brokerage/tt";
import {
  ORDER_TYPES,
  ORDER_TYPE_FLAG_KEY,
  ORDER_TYPE_LABELS,
} from "@/lib/tt/orderType";

interface AssociatedMetadataSectionProps {
  metadata: TtOrderMetadata | null;
}

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="grid">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium break-words">
        {value == null || value === "" ? "-" : value}
      </span>
    </div>
  );
}

/**
 * Read-only summary of the AssociatedMetaData record + its SpecifiedCustomers.
 * Used by the Active Orders side panel where the attach form is hidden but the
 * user still wants to see what was previously associated.
 */
export function AssociatedMetadataSection({ metadata }: AssociatedMetadataSectionProps) {
  if (metadata == null) {
    return (
      <p className="text-sm text-muted-foreground">No associated metadata.</p>
    );
  }

  const expectedTypes = ORDER_TYPES.filter((t) => metadata[ORDER_TYPE_FLAG_KEY[t]] === true);

  return (
    <div className="flex flex-col gap-3">
      <div>
        <span className="text-xs text-muted-foreground">Expected Type</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {expectedTypes.length === 0 ? (
            <span className="text-sm font-medium">-</span>
          ) : (
            expectedTypes.map((t) => (
              <Badge key={t} variant="outline" className="text-xs">
                {ORDER_TYPE_LABELS[t]}
              </Badge>
            ))
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {metadata.expected_otc && (
          <Field label="OTC Contract Type" value={metadata.otc_contract_type} />
        )}
        <Field label="Crop Year" value={metadata.crop_year} />
        <Field label="Should Ignore (Error)" value={metadata.should_ignore_order ? "Yes" : "No"} />
      </div>

      <div>
        <span className="text-xs text-muted-foreground">Customers</span>
        {metadata.SpecifiedCustomers.length === 0 ? (
          <p className="text-sm font-medium mt-1">-</p>
        ) : (
          <div className="flex flex-col gap-1 mt-1">
            {metadata.SpecifiedCustomers.map((c) => (
              <div
                key={c.customer_id}
                className="flex flex-row items-center justify-between gap-2 px-2 py-1 rounded-md border bg-muted/30"
              >
                <span className="text-sm font-medium break-words">
                  {c.Customer?.long_name ?? c.customer_id}
                </span>
                <span className="text-sm tabular-nums text-muted-foreground shrink-0">
                  {c.split_percentage}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
